<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useDealsStore } from 'dashboard/stores/deals';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();

const globalStore = useStore();
const companiesStore = useCompaniesStore();
const dealsStore = useDealsStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);

const formData = ref({
  title: '',
  task_type: 'call',
  priority: 'medium',
  due_date: '',
  user_id: '',
  taskable_type: '',
  taskable_id: '',
});

const rules = {
  title: { required },
};

const v$ = useVuelidate(rules, formData);

const isFormInvalid = computed(() => v$.value.$invalid);

const taskTypeOptions = [
  { value: 'call', label: t('TASKS.TYPES.CALL') },
  { value: 'email', label: t('TASKS.TYPES.EMAIL') },
  { value: 'meeting', label: t('TASKS.TYPES.MEETING') },
  { value: 'follow_up', label: t('TASKS.TYPES.FOLLOW_UP') },
  { value: 'other', label: t('TASKS.TYPES.OTHER') },
];

const priorityOptions = [
  { value: 'low', label: t('TASKS.PRIORITIES.LOW') },
  { value: 'medium', label: t('TASKS.PRIORITIES.MEDIUM') },
  { value: 'high', label: t('TASKS.PRIORITIES.HIGH') },
];

// Agent selector options from Vuex store
const assigneeOptions = computed(() => {
  const agents = globalStore.getters['agents/getAgents'] || [];
  return agents.map(agent => ({
    value: agent.id,
    label: agent.name || agent.email || t('TASKS.UNTITLED'),
  }));
});

// Related entity options (combining deals and companies)
const relatedEntityOptions = computed(() => {
  const options = [];
  
  // Add deals
  const deals = dealsStore.getDealsList || [];
  deals.forEach(deal => {
    options.push({
      value: `Deal:${deal.id}`,
      label: `${deal.title || t('TASKS.UNTITLED')} (Deal)`,
      type: 'Deal',
      id: deal.id,
    });
  });
  
  // Add companies
  const companies = companiesStore.getCompaniesList || [];
  companies.forEach(company => {
    options.push({
      value: `Company:${company.id}`,
      label: `${company.name || t('TASKS.UNTITLED')} (Company)`,
      type: 'Company',
      id: company.id,
    });
  });
  
  return options;
});

// Handle related entity selection
const handleRelatedEntityChange = selectedValue => {
  if (!selectedValue) {
    formData.value.taskable_type = '';
    formData.value.taskable_id = '';
    return;
  }
  
  const selected = relatedEntityOptions.value.find(opt => opt.value === selectedValue);
  if (selected) {
    formData.value.taskable_type = selected.type;
    formData.value.taskable_id = selected.id;
  }
};

// Get current related entity value
const currentRelatedEntity = computed(() => {
  if (!formData.value.taskable_type || !formData.value.taskable_id) return '';
  return `${formData.value.taskable_type}:${formData.value.taskable_id}`;
});

const open = () => {
  resetForm();
  // Preload data for selectors
  dealsStore.get({ page: 1 });
  companiesStore.get({ page: 1 });
  globalStore.dispatch('agents/get');
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const resetForm = () => {
  formData.value = {
    title: '',
    task_type: 'call',
    priority: 'medium',
    due_date: '',
    user_id: '',
    taskable_type: '',
    taskable_id: '',
  };
  if (v$.value) {
    v$.value.$reset();
  }
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;
  try {
    const data = { ...formData.value };
    
    // Only include relation IDs if they have values
    data.user_id = data.user_id || null;
    data.taskable_id = data.taskable_id || null;
    
    // Remove null values to avoid foreign key violations
    Object.keys(data).forEach(key => {
      if (data[key] === null || data[key] === '') {
        delete data[key];
      }
    });
    
    emit('create', data);
    close();
  } catch (error) {
    useAlert(t('TASKS.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const onSuccess = () => {
  resetForm();
  close();
};

defineExpose({
  dialogRef,
  open,
  close,
  onSuccess,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('TASKS.CREATE_DIALOG.TITLE')"
    :description="$t('TASKS.CREATE_DIALOG.DESCRIPTION')"
  >
    <div class="flex flex-col gap-4 py-4">
      <Input
        v-model="formData.title"
        :label="$t('TASKS.FORM.TITLE_LABEL')"
        :placeholder="$t('TASKS.TITLE_PLACEHOLDER')"
        :error="v$.title.$error ? $t('TASKS.FORM.TITLE_REQUIRED') : ''"
        autofocus
      />
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('TASKS.FORM.TYPE_LABEL') }}
          </label>
          <ComboBox
            v-model="formData.task_type"
            :options="taskTypeOptions"
            :placeholder="$t('TASKS.FORM.TYPE_LABEL')"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('TASKS.FORM.PRIORITY_LABEL') }}
          </label>
          <ComboBox
            v-model="formData.priority"
            :options="priorityOptions"
            :placeholder="$t('TASKS.FORM.PRIORITY_LABEL')"
          />
        </div>
      </div>
      
      <Input
        v-model="formData.due_date"
        type="datetime-local"
        :label="$t('TASKS.FORM.DUE_DATE_LABEL')"
      />

      <!-- Assignee selector -->
      <div class="flex flex-col gap-2 pt-2 border-t border-n-strong">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TASKS.FORM.ASSIGNEE_LABEL') }}
        </label>
        <ComboBox
          v-model="formData.user_id"
          :options="assigneeOptions"
          :placeholder="$t('TASKS.FORM.ASSIGNEE_PLACEHOLDER')"
        />
      </div>

      <!-- Related to selector -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TASKS.FORM.RELATED_TO_LABEL') }}
        </label>
        <ComboBox
          :model-value="currentRelatedEntity"
          :options="relatedEntityOptions"
          :placeholder="$t('TASKS.FORM.RELATED_TO_PLACEHOLDER')"
          @update:model-value="handleRelatedEntityChange"
        />
      </div>
    </div>
    
    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          :label="$t('TASKS.CANCEL')"
          variant="ghost"
          @click="close"
        />
        <Button
          :label="$t('TASKS.CREATE_DIALOG.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isFormInvalid || isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
